/**
 * Facturen in het dossier (SPEC §7.8): de lijst, en een formulier om een
 * nieuwe factuur te maken, eerst te bekijken en dan te mailen.
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiFout, api, type Factuurinvoer, type Factuurregel, type Factuurvoorstel } from '../../api';
import { Bevestigknop, Datum, Euro, Knop, Melding, Veld } from '../../componenten/basis';
import { alsEuro, uitInvoer } from '../../lib/geld';

export function Facturen({ heldId, bedrijf, mag, naVerandering }: { heldId: string; bedrijf: boolean; mag: boolean; naVerandering: () => void }) {
  const [lijst, zetLijst] = useState<Factuurregel[] | null>(null);
  const [voorstel, zetVoorstel] = useState<Factuurvoorstel | null>(null);
  const [melding, zetMelding] = useState<{ soort: 'goed' | 'fout'; tekst: string } | null>(null);

  const laad = useCallback(() => {
    api.haal<Factuurregel[]>(`/api/beheer/helden/${heldId}/facturen`).then(zetLijst).catch(() => zetLijst([]));
  }, [heldId]);
  useEffect(laad, [laad]);

  async function open() {
    zetMelding(null);
    try {
      zetVoorstel(await api.haal<Factuurvoorstel>(`/api/beheer/helden/${heldId}/facturen/voorstel`));
    } catch (f) {
      zetMelding({ soort: 'fout', tekst: (f as Error).message });
    }
  }

  async function opnieuw(f: Factuurregel) {
    try {
      const uit = await api.stuur<{ facturen: Factuurregel[] }>(`/api/beheer/facturen/${f.id}/versturen`);
      zetLijst(uit.facturen);
      zetMelding({ soort: 'goed', tekst: `Factuur ${f.nummer} is opnieuw verstuurd naar ${f.ontvanger_email}.` });
      naVerandering();
    } catch (fout) {
      zetMelding({ soort: 'fout', tekst: (fout as Error).message });
    }
  }

  return (
    <section className="kaart p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl">Facturen</h2>
        {mag && !voorstel && (
          <Knop soort="stil" klein onClick={open}>
            Factuur maken
          </Knop>
        )}
      </div>

      <p className="mt-1 text-sm text-inkt-zacht">
        {bedrijf
          ? 'Na elke betaling gaat er vanzelf een factuur naar dit bedrijf. Hier maak je er een met de hand, bijvoorbeeld voor een ander adres.'
          : 'Een particulier krijgt geen factuur vanzelf. Vraagt iemand erom, dan maak en mail je hem hier.'}
      </p>

      {melding && (
        <div className="mt-3">
          <Melding soort={melding.soort}>{melding.tekst}</Melding>
        </div>
      )}

      {voorstel && (
        <Factuurformulier
          heldId={heldId}
          voorstel={voorstel}
          onKlaar={(uit) => {
            zetVoorstel(null);
            zetLijst(uit.facturen);
            zetMelding({
              soort: uit.verstuurd ? 'goed' : 'fout',
              tekst: uit.verstuurd
                ? `Factuur ${uit.nummer} is gemaakt en verstuurd.`
                : `Factuur ${uit.nummer} is gemaakt, maar de mail is niet verstuurd. Probeer het opnieuw vanuit de lijst.`,
            });
            naVerandering();
          }}
          onAnnuleer={() => zetVoorstel(null)}
        />
      )}

      {lijst && lijst.length === 0 && !voorstel && <p className="mt-3 text-sm text-inkt-zacht">Nog geen facturen.</p>}
      {lijst && lijst.length > 0 && (
        <table className="tabel mt-3">
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Omschrijving</th>
              <th>Bedrag</th>
              <th>Verstuurd</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lijst.map((f) => (
              <tr key={f.id}>
                <td>
                  <span className="font-medium">{f.nummer}</span>
                  <span className="block text-xs text-inkt-zacht">
                    <Datum waarde={f.datum} />
                  </span>
                </td>
                <td>
                  {f.omschrijving}
                  <span className="block text-xs text-inkt-zacht">{f.ontvanger_naam}</span>
                </td>
                <td>
                  <Euro cent={f.bedrag_cent} />
                </td>
                <td className="text-inkt-zacht">
                  {f.verstuurd_op ? (
                    <>
                      <Datum waarde={f.verstuurd_op} />
                      <span className="block text-xs">{f.ontvanger_email}</span>
                    </>
                  ) : (
                    'nee'
                  )}
                </td>
                <td className="text-right">
                  <span className="flex flex-wrap justify-end gap-2">
                    <a href={`/api/beheer/facturen/${f.id}/pdf`} className="knop knop-stil knop-klein">
                      PDF
                    </a>
                    {mag && (
                      <Bevestigknop soort="stil" vraag={`Opnieuw mailen naar ${f.ontvanger_email}?`} onBevestig={() => opnieuw(f)}>
                        Opnieuw mailen
                      </Bevestigknop>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Factuurformulier({
  heldId,
  voorstel,
  onKlaar,
  onAnnuleer,
}: {
  heldId: string;
  voorstel: Factuurvoorstel;
  onKlaar: (uit: { nummer: string; verstuurd: boolean; facturen: Factuurregel[] }) => void;
  onAnnuleer: () => void;
}) {
  const eerste = voorstel.bijdragen[0] ?? null;
  const [bijdrageId, zetBijdrageId] = useState<string>(eerste?.id ?? '');
  const [naam, zetNaam] = useState(voorstel.ontvanger_naam);
  const [adres, zetAdres] = useState(voorstel.ontvanger_adres ?? '');
  const [email, zetEmail] = useState(voorstel.ontvanger_email);
  const [omschrijving, zetOmschrijving] = useState(eerste?.omschrijving ?? voorstel.omschrijving_los);
  const [bedragTekst, zetBedragTekst] = useState(eerste ? bedragAlsTekst(eerste.bedrag_cent) : '');
  const [fout, zetFout] = useState<string | null>(null);
  const [bezig, zetBezig] = useState(false);

  const gekozen = voorstel.bijdragen.find((b) => b.id === bijdrageId) ?? null;
  const bedrag = uitInvoer(bedragTekst);

  function kies(id: string) {
    zetBijdrageId(id);
    const b = voorstel.bijdragen.find((x) => x.id === id);
    zetOmschrijving(b ? b.omschrijving : voorstel.omschrijving_los);
    if (b) zetBedragTekst(bedragAlsTekst(b.bedrag_cent));
  }

  function invoer(): Factuurinvoer {
    return {
      bijdrage_id: bijdrageId || null,
      ontvanger_naam: naam,
      ontvanger_adres: adres,
      ontvanger_email: email,
      omschrijving,
      bedrag_cent: bedrag ?? undefined,
    };
  }

  async function voorbeeld() {
    zetFout(null);
    // Het venster meteen openen, anders houdt de popupblokkering het tegen.
    const venster = window.open('', '_blank');
    try {
      const antwoord = await fetch(`/api/beheer/helden/${heldId}/facturen/voorbeeld`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(invoer()),
      });
      if (!antwoord.ok) {
        const inhoud = (await antwoord.json().catch(() => null)) as { fout?: string } | null;
        throw new Error(inhoud?.fout ?? 'Het voorbeeld lukte niet.');
      }
      const url = URL.createObjectURL(await antwoord.blob());
      if (venster) venster.location.href = url;
      else window.location.href = url;
    } catch (f) {
      venster?.close();
      zetFout((f as Error).message);
    }
  }

  async function maak() {
    zetFout(null);
    zetBezig(true);
    try {
      onKlaar(await api.stuur(`/api/beheer/helden/${heldId}/facturen`, invoer()));
    } catch (f) {
      zetFout(f instanceof ApiFout ? f.message : 'Er ging iets mis.');
    } finally {
      zetBezig(false);
    }
  }

  return (
    <div className="mt-4 space-y-4 rounded-xl border border-lijn bg-vlak p-4">
      <Veld etiket="Voor welke bijdrage">
        <select className="veld" value={bijdrageId} onChange={(e) => kies(e.target.value)}>
          {voorstel.bijdragen.map((b) => (
            <option key={b.id} value={b.id}>
              {b.label} - {alsEuro(b.bedrag_cent)}
            </option>
          ))}
          <option value="">Losse factuur, zonder bijdrage</option>
        </select>
      </Veld>
      {gekozen && gekozen.gefactureerd.length > 0 && (
        <Melding>Voor deze bijdrage is al een factuur gemaakt: {gekozen.gefactureerd.join(', ')}. Een nieuwe krijgt een nieuw nummer.</Melding>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Veld etiket="Aan">
          <input className="veld" value={naam} onChange={(e) => zetNaam(e.target.value)} />
        </Veld>
        <Veld etiket="Naar e-mailadres" hulp="Hier gaat de factuur heen, bijvoorbeeld de administratie.">
          <input className="veld" type="email" value={email} onChange={(e) => zetEmail(e.target.value)} />
        </Veld>
      </div>
      <Veld etiket="Factuuradres" hulp="Meerdere regels. Wordt onthouden voor de volgende keer.">
        <textarea className="veld" rows={3} value={adres} onChange={(e) => zetAdres(e.target.value)} placeholder={'t.a.v. de administratie\nStraat 1\n1234 AB Plaats'} />
      </Veld>
      <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
        <Veld etiket="Omschrijving">
          <input className="veld" value={omschrijving} onChange={(e) => zetOmschrijving(e.target.value)} />
        </Veld>
        <Veld etiket="Bedrag" fout={bedragTekst && bedrag === null ? 'Geen bedrag' : null}>
          <div className="flex items-center gap-2">
            <span className="text-inkt-zacht">€</span>
            <input className="veld max-w-[8rem]" inputMode="decimal" value={bedragTekst} onChange={(e) => zetBedragTekst(e.target.value)} />
          </div>
        </Veld>
      </div>
      {fout && <Melding soort="fout">{fout}</Melding>}
      <div className="flex flex-wrap items-center gap-3">
        <Knop soort="stil" klein onClick={voorbeeld}>
          Voorbeeld bekijken
        </Knop>
        <Bevestigknop soort="goud" vraag={`Factuur vastleggen en mailen naar ${email}?`} onBevestig={maak}>
          {bezig ? 'Bezig…' : 'Maken en mailen'}
        </Bevestigknop>
        <Knop soort="stil" klein onClick={onAnnuleer}>
          Annuleren
        </Knop>
      </div>
      <p className="text-xs text-inkt-zacht">
        Het nummer wordt pas toegekend bij het vastleggen. Daarna verandert de factuur niet meer; een correctie is een nieuwe factuur.
      </p>
    </div>
  );
}

function bedragAlsTekst(cent: number): string {
  return alsEuro(cent).replace(/^€\s?/, '').replace(',-', '').replace(/ /g, '').trim();
}
